import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFeature } from "../context/FeatureContext";
import axios from "axios";
import FeedbackButton from "../components/FeedbackButton";
import { PWAInstallBanner } from "../components/PWAInstall";
import { PushNotificationButton } from "../components/PushNotificationButton";
import { 
  Tag, Thermometer, ShoppingCart, Truck, LogOut,
  BookOpen, BookOpenCheck, Apple, CalendarDays, ChevronRight, CheckCircle2, Settings, Wrench,
  Sun, Clock, Users, Bell, AlertTriangle, ClipboardList, FolderOpen, X,
  Sparkles, Shield, Package
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONAL = [
  { key: "presenze", label: "Timbra", icon: Clock, gradient: "linear-gradient(180deg, #6DDBA6 0%, #38B27A 100%)", shadow: "rgba(56,178,122,0.3)", path: "/operatore/presenze", gateFeature: "presenze" },
  { key: "miei_turni", label: "I miei turni", icon: CalendarDays, gradient: "linear-gradient(180deg, #7BC8F6 0%, #3A9FDB 100%)", shadow: "rgba(58,159,219,0.3)", path: "/operatore/turni", gateFeature: "turni" },
  { key: "ferie", label: "Ferie &\nPermessi", icon: Sun, gradient: "linear-gradient(180deg, #FFD06A 0%, #E5A520 100%)", shadow: "rgba(229,165,32,0.3)", path: "/operatore/ferie", gateFeature: "ferie_permessi" },
  { key: "avvisi_scorte", label: "Avvisi\nscorte", icon: Bell, gradient: "linear-gradient(180deg, #FF8FA3 0%, #E05573 100%)", shadow: "rgba(224,85,115,0.3)", path: "/operatore/avvisi-scorte" },
  { key: "segnalazioni", label: "Segnalazioni", icon: AlertTriangle, gradient: "linear-gradient(180deg, #F87171 0%, #DC2626 100%)", shadow: "rgba(220,38,38,0.3)", path: "/operatore/segnalazioni" },
  { key: "miei_documenti", label: "I miei\nDocumenti", icon: FolderOpen, gradient: "linear-gradient(180deg, #818CF8 0%, #6366F1 100%)", shadow: "rgba(99,102,241,0.3)", path: "/operatore/documenti" },
  { key: "tecnici", label: "Contatti\nutili", icon: Wrench, gradient: "linear-gradient(180deg, #A0AEC0 0%, #64748B 100%)", shadow: "rgba(100,116,139,0.3)", path: "/anagrafica/tecnici" },
];

// Tiles aggiuntive per cucina e laboratorio
const ROLE_DEFAULTS = [
  { key: "ricette_view", label: "Ricette", icon: BookOpen, gradient: "linear-gradient(180deg, #5DD4C8 0%, #2AA89C 100%)", shadow: "rgba(42,168,156,0.3)", path: "/anagrafica/ricette", gateFeature: "ricette" },
  { key: "lotti", label: "Lotti", icon: Tag, gradient: "linear-gradient(180deg, #D4A0F5 0%, #A855F7 100%)", shadow: "rgba(168,85,247,0.3)", path: "/etichette", gateFeature: "tracciabilita_lotti" },
];

const ASSIGNED = {
  turni: { label: "Gestione\nTurni", icon: Users, gradient: "linear-gradient(180deg, #B4A0F5 0%, #8B6CE0 100%)", shadow: "rgba(139,108,224,0.3)", path: "/personale/turni", gateFeature: "turni" },
  creazione_ricette: { label: "Gestione\nRicette", icon: BookOpen, gradient: "linear-gradient(180deg, #FFAB6A 0%, #E07B30 100%)", shadow: "rgba(224,123,48,0.3)", path: "/anagrafica/ricette?gestione=1", gateFeature: "ricette" },
  gestione_menu: { label: "Gestione\nMenu di Vendita", icon: BookOpenCheck, gradient: "linear-gradient(180deg, #C4A5F7 0%, #8B5CF6 100%)", shadow: "rgba(139,92,246,0.3)", path: "/menu", gateFeature: "menu_vendita" },
  ingredienti: { label: "Materie\nprime", icon: Apple, gradient: "linear-gradient(180deg, #FFAB6A 0%, #E07B30 100%)", shadow: "rgba(224,123,48,0.3)", path: "/anagrafica/ingredienti", gateFeature: "materie_prime" },
  ordini_fornitori: { label: "Ordini\nFornitori", icon: ClipboardList, gradient: "linear-gradient(180deg, #F59E0B 0%, #D97706 100%)", shadow: "rgba(217,119,6,0.3)", path: "/operatore/ordini", gateFeature: "fornitori" },
  acquisti: { label: "Acquisti", icon: ShoppingCart, gradient: "linear-gradient(180deg, #F5A0D0 0%, #DB2777 100%)", shadow: "rgba(219,39,119,0.3)", path: "/acquisti/food", gateFeature: "materie_prime" },
  fornitori: { label: "Fornitori", icon: Truck, gradient: "linear-gradient(180deg, #7B9CEB 0%, #4560C7 100%)", shadow: "rgba(69,96,199,0.3)", path: "/acquisti/fornitori", gateFeature: "fornitori" },
};

const OperatoreDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, localeCorrente, getAuthHeaders, token } = useAuth();
  const { hasFeature } = useFeature();
  const [stats, setStats] = useState({ pulizie: 0, pulizieCompletate: 0, temperature: 0, temperatureCompletate: 0, gestione: 0, gestioneCompletate: 0, preparazioni: 0, preparazioniCompletate: 0 });
  const [gating, setGating] = useState({ requires_timbra: false, has_turni_oggi: true, presenze_gating_attivo: false });
  const [badges, setBadges] = useState({});
  const [tempAlerts, setTempAlerts] = useState([]);
  const [opNotifiche, setOpNotifiche] = useState([]);
  // stato rimosso

  const funzionalitaUtente = user?.funzionalita || [];
  const assignedKeys = Object.keys(ASSIGNED).filter(f => {
    const item = ASSIGNED[f];
    // Must be assigned to user
    const isAssigned = funzionalitaUtente.includes(f) || (f === "creazione_ricette" && funzionalitaUtente.includes("ricette"));
    if (!isAssigned) return false;
    // Must be from a purchased package
    if (item.gateFeature && !hasFeature(item.gateFeature)) return false;
    return true;
  });
  const isRoleCucinaOrLab = user?.ruolo === "cucina" || user?.ruolo === "laboratorio";

  // Filter tiles by purchased packages (via FeatureContext) AND assigned funzionalita (via user)
  const isAvailable = (item) => {
    if (item.gateFeature && !hasFeature(item.gateFeature)) return false;
    if (item.gateFunc && !funzionalitaUtente.includes(item.gateFunc)) return false;
    return true;
  };
  const visiblePersonal = PERSONAL.filter(isAvailable);
  const visibleRoleDefaults = ROLE_DEFAULTS.filter(isAvailable);

  useEffect(() => {
    if (localeCorrente?.id) {
      fetchStats();
      fetchBadges();
      fetchTempAlerts();
      fetchOpNotifiche();
    }
  }, [localeCorrente?.id]);

  const fetchOpNotifiche = async () => {
    try {
      const res = await axios.get(`${API}/operatore/notifiche?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() });
      setOpNotifiche(res.data || []);
    } catch (e) { console.error("op notifiche error", e); }
  };

  const handleDismissNotifica = async (id) => {
    try {
      await axios.put(`${API}/operatore/notifiche/${id}/read`, {}, { headers: getAuthHeaders() });
      setOpNotifiche(prev => prev.filter(n => n.id !== id));
    } catch (e) { console.error(e); }
  };

  const fetchTempAlerts = async () => {
    try {
      const res = await axios.get(`${API}/temperature-alerts?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() });
      setTempAlerts((res.data || []).filter(a => a.stato !== "risolto" && a.stato !== "archiviato"));
    } catch (e) { console.error("temp alerts error", e); }
  };

  const fetchStats = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await axios.get(`${API}/calendario-attivita?locale_id=${localeCorrente.id}&data=${today}`, { headers: getAuthHeaders() });
      const s = res.data.stats || {};
      setStats({
        pulizie: s.pulizie_totali || 0, pulizieCompletate: s.pulizie_completate || 0,
        temperature: s.temperature_totali || 0, temperatureCompletate: s.temperature_completate || 0,
        gestione: s.gestione_totali || 0, gestioneCompletate: s.gestione_completate || 0,
        preparazioni: s.preparazioni_totali || 0, preparazioniCompletate: s.preparazioni_completate || 0,
      });
      setGating(res.data.gating || { requires_timbra: false, has_turni_oggi: true, presenze_gating_attivo: false });
    } catch (e) { console.error(e); }
  };

  const fetchBadges = async () => {
    const headers = getAuthHeaders();
    const newBadges = {};

    try {
      // Fetch dashboard notifications (temperature, scorte, pulizie, compiti)
      const notifRes = await axios.get(
        `${API}/dashboard/notifications?locale_id=${localeCorrente.id}`,
        { headers }
      );
      const n = notifRes.data;
      if (n.temperature?.count > 0) newBadges.frigoriferi = n.temperature.count;
      if (n.scorte?.count > 0) newBadges.avvisi_scorte = n.scorte.count;
      if (n.pulizie_mancate?.count > 0) newBadges.pulizie = n.pulizie_mancate.count;
      if (n.compiti_mancati?.count > 0) newBadges.compiti = n.compiti_mancati.count;
    } catch (e) { console.error("notif error", e); }

    try {
      // Fetch pending ferie/permessi for this user
      const ferieRes = await axios.get(
        `${API}/personale/assenze?locale_id=${localeCorrente.id}&utente_id=${user?.id}&stato=pending`,
        { headers }
      );
      if (ferieRes.data?.length > 0) newBadges.ferie = ferieRes.data.length;
    } catch (e) { console.error("ferie error", e); }

    try {
      const segRes = await axios.get(
        `${API}/segnalazioni-locale/conteggi?locale_id=${localeCorrente.id}`,
        { headers }
      );
      const segData = segRes.data;
      const segCount = (segData?.risolte_non_viste || 0);
      if (segCount > 0) newBadges.segnalazioni = segCount;
    } catch (e) { console.error("segnalazioni error", e); }

    // Badge richieste pendenti per chi ha gestione turni
    if (user?.funzionalita?.includes("turni") || user?.funzionalita?.includes("approva_richieste")) {
      try {
        const richRes = await axios.get(
          `${API}/personale/richieste-pendenti/count?locale_id=${localeCorrente.id}`,
          { headers }
        );
        if (richRes.data?.totale > 0) newBadges.turni = richRes.data.totale;
      } catch (e) { console.error("richieste pendenti error", e); }
    }

    // Badge documenti: notifiche non lette per l'operatore
    try {
      const docRes = await axios.get(
        `${API}/miei-documenti/notifiche/count`,
        { headers }
      );
      if (docRes.data?.count > 0) newBadges.miei_documenti = docRes.data.count;
    } catch (e) { console.error("notif doc error", e); }

    setBadges(newBadges);
  };

  const total = stats.pulizie + stats.temperature + stats.gestione + stats.preparazioni;
  const done = stats.pulizieCompletate + stats.temperatureCompletate + stats.gestioneCompletate + stats.preparazioniCompletate;
  const remaining = total - done;
  const pct = total > 0 ? (done / total) * 100 : 0;
  const allDone = total > 0 && remaining === 0;

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Buongiorno";
    if (h < 18) return "Buon pomeriggio";
    return "Buonasera";
  };

  const Tile = ({ icon: Icon, label, gradient, shadow, onClick, testId, badge }) => (
    <button
      onClick={onClick}
      className="relative flex flex-col items-center justify-center gap-2.5 rounded-[22px] py-5 px-2 transition-all duration-150 active:scale-[0.96]"
      style={{ background: gradient, boxShadow: `0 6px 20px ${shadow}`, minHeight: "120px" }}
      data-testid={testId}
    >
      {badge > 0 && (
        <span
          className="absolute top-1 right-1 min-w-[24px] h-[24px] flex items-center justify-center rounded-full text-[12px] font-bold leading-none px-1"
          style={{ background: "#ff3b30", color: "#fff", boxShadow: "0 2px 8px rgba(255,59,48,0.45)" }}
          data-testid={`badge-${testId}`}
        >
          {badge}
        </span>
      )}
      <Icon className="h-8 w-8" style={{ color: "#ffffff", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.15))" }} strokeWidth={2.2} />
      <span className="text-[13px] font-bold leading-tight text-center whitespace-pre-line" style={{ color: "#ffffff", textShadow: "0 1px 3px rgba(0,0,0,0.12)" }}>
        {label}
      </span>
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#f0f0f5", overflowX: "hidden" }} data-testid="operatore-dashboard">
      {/* PWA Install Banner */}
      <PWAInstallBanner area="operatore" />
      {/* Header */}
      <header className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/anagrafica/profilo")} data-testid="goto-profile-btn">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg" style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)" }}>
              {(user?.nome || user?.email || user?.telefono || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "#9ca3af" }}>{greeting()}</p>
              <h1 className="text-[17px] font-bold leading-tight" style={{ color: "#1f2937" }}>{user?.nome || user?.email || user?.telefono}</h1>
              {localeCorrente && <p className="text-[11px] font-medium" style={{ color: "#9ca3af" }}>{localeCorrente.nome}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PushNotificationButton token={token} />
            <button onClick={() => { logout(); navigate("/login"); }} className="w-10 h-10 rounded-2xl bg-white/80 flex items-center justify-center shadow-sm" data-testid="logout-btn">
              <LogOut className="h-4 w-4" style={{ color: "#9ca3af" }} />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-lg mx-auto w-full px-4 pb-8">

        {/* ══ ALERT TEMPERATURE BANNER ══ */}
        {tempAlerts.length > 0 && (
          <div className="mt-2 mb-3 space-y-2.5" data-testid="temp-alerts-banner">
            {tempAlerts.map(alert => (
              <button
                key={alert.id}
                onClick={() => navigate("/frigoriferi")}
                className="w-full text-left rounded-[20px] overflow-hidden active:scale-[0.98] transition-transform"
                style={{
                  background: alert.stato === "da_tecnico"
                    ? "linear-gradient(135deg, #DC2626 0%, #991B1B 100%)"
                    : alert.stato === "in_attesa"
                    ? "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
                    : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                  boxShadow: "0 6px 24px rgba(220,38,38,0.35)"
                }}
                data-testid={`temp-alert-banner-${alert.id}`}
              >
                <div className="px-5 py-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(8px)" }}>
                    <Thermometer className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(255,255,255,0.25)", color: "#fff" }}>
                        {alert.stato === "da_tecnico" ? "CHIAMA TECNICO" : alert.stato === "in_attesa" ? "DA VERIFICARE" : "ALERT"}
                      </span>
                    </div>
                    <p className="text-[15px] font-bold text-white leading-snug truncate">
                      {alert.frigorifero_nome}
                    </p>
                    <p className="text-[12px] font-medium" style={{ color: "rgba(255,255,255,0.8)" }}>
                      {alert.temperatura}°C — Range: {alert.range_min}° / {alert.range_max}°C
                    </p>
                  </div>
                  <div className="shrink-0 flex flex-col items-center">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: "rgba(255,255,255,0.25)" }}>
                      <ChevronRight className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-[9px] font-bold text-white/70 mt-1">Verifica</span>
                  </div>
                </div>
                {(alert.istruzioni || alert.risposta_admin) && (
                  <div className="px-5 pb-3 -mt-1">
                    <div className="p-2.5 rounded-xl" style={{ background: "rgba(255,255,255,0.15)" }}>
                      <p className="text-[11px] text-white/90">
                        <span className="font-bold">Istruzioni:</span> {alert.risposta_admin || alert.istruzioni}
                      </p>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* ══ NOTIFICHE OPERATORE (Lotti scadenza, ecc.) ══ */}
        {opNotifiche.length > 0 && (
          <div className="mt-2 mb-3 space-y-2" data-testid="op-notifiche-banner">
            {opNotifiche.map(n => (
              <div
                key={n.id}
                className="rounded-2xl overflow-hidden"
                style={{
                  background: n.tipo === "lotto_scadenza"
                    ? "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
                    : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                  boxShadow: n.tipo === "lotto_scadenza" ? "0 4px 16px rgba(245,158,11,0.3)" : "0 4px 16px rgba(239,68,68,0.3)"
                }}
              >
                <div className="px-4 py-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.2)" }}>
                    {n.tipo === "lotto_scadenza" ? <Tag className="h-5 w-5 text-white" /> : <Thermometer className="h-5 w-5 text-white" />}
                  </div>
                  <button onClick={() => { handleDismissNotifica(n.id); if (n.riferimento_link) navigate(n.riferimento_link); }}
                    className="flex-1 min-w-0 text-left">
                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-white/70">{n.titolo}</p>
                    <p className="text-[13px] font-semibold text-white leading-snug">{n.messaggio}</p>
                  </button>
                  <button onClick={() => handleDismissNotifica(n.id)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.2)" }}
                    data-testid={`dismiss-notif-${n.id}`}>
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Activity Card */}
        <button
          onClick={() => {
            if (gating.requires_timbra) {
              navigate("/operatore/presenze");
            } else if (!gating.has_turni_oggi) {
              navigate("/operatore/turni");
            } else {
              navigate("/calendario-attivita");
            }
          }}
          className="w-full mt-2 text-left"
          data-testid="activity-card"
        >
          <div className="p-5 rounded-[24px] relative overflow-hidden"
            style={{
              background: gating.requires_timbra
                ? "linear-gradient(180deg, #94A3B8 0%, #64748B 100%)"
                : !gating.has_turni_oggi
                  ? "linear-gradient(180deg, #CBD5E1 0%, #94A3B8 100%)"
                  : allDone
                    ? "linear-gradient(180deg, #6DDBA6 0%, #38B27A 100%)"
                    : "linear-gradient(180deg, #FF8A6B 0%, #E85D3A 100%)",
              boxShadow: gating.requires_timbra || !gating.has_turni_oggi
                ? "0 8px 28px rgba(100,116,139,0.35)"
                : allDone
                  ? "0 8px 28px rgba(56,178,122,0.35)"
                  : "0 8px 28px rgba(232,93,58,0.35)"
            }}>
            <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full" style={{ background: "rgba(255,255,255,0.12)" }} />
            {gating.requires_timbra ? (
              <div className="flex items-center gap-3 relative z-10">
                <Clock className="h-8 w-8" style={{ color: "#ffffff" }} />
                <div className="flex-1">
                  <p className="font-bold text-lg" style={{ color: "#ffffff" }}>Timbra l'entrata</p>
                  <p className="text-sm leading-tight" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Le tue attività compariranno dopo aver timbrato
                  </p>
                </div>
                <ChevronRight className="h-5 w-5" style={{ color: "rgba(255,255,255,0.7)" }} />
              </div>
            ) : !gating.has_turni_oggi ? (
              <div className="flex items-center gap-3 relative z-10">
                <CalendarDays className="h-8 w-8" style={{ color: "#ffffff" }} />
                <div className="flex-1">
                  <p className="font-bold text-lg" style={{ color: "#ffffff" }}>Nessun turno oggi</p>
                  <p className="text-sm leading-tight" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Buon riposo! Nessuna mansione assegnata
                  </p>
                </div>
              </div>
            ) : allDone ? (
              <div className="flex items-center gap-3 relative z-10">
                <CheckCircle2 className="h-8 w-8" style={{ color: "#ffffff" }} />
                <div>
                  <p className="font-bold text-lg" style={{ color: "#ffffff" }}>Tutto fatto!</p>
                  <p className="text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>Ottimo lavoro per oggi</p>
                </div>
              </div>
            ) : (
              <div className="relative z-10">
                <div className="flex items-end justify-between mb-2.5">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.8)" }}>Oggi</p>
                    <p className="font-extrabold text-[22px]" style={{ color: "#ffffff" }}>{remaining > 0 ? `${remaining} da fare` : "Nessuna attività"}</p>
                  </div>
                  <div className="rounded-xl px-3.5 py-1.5" style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(8px)" }}>
                    <span className="font-extrabold text-xl" style={{ color: "#ffffff" }}>{done}</span>
                    <span className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>/{total}</span>
                  </div>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.25)" }}>
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: "#ffffff" }} />
                </div>
                <div className="flex items-center justify-between mt-2.5">
                  <p className="text-[11px] font-medium" style={{ color: "rgba(255,255,255,0.7)" }}>Apri calendario attività</p>
                  <ChevronRight className="h-4 w-4" style={{ color: "rgba(255,255,255,0.5)" }} />
                </div>
              </div>
            )}
          </div>
        </button>

        {/* Personal tiles */}
        <p className="text-[11px] font-bold uppercase tracking-widest mt-6 mb-3 px-1" style={{ color: "#9ca3af" }}>Area personale</p>
        <div className="grid grid-cols-3 gap-3">
          {visiblePersonal.map((item) => (
            <Tile key={item.key} icon={item.icon} label={item.label} gradient={item.gradient} shadow={item.shadow}
              badge={badges[item.key] || 0}
              onClick={() => navigate(item.path)} testId={`func-btn-${item.key}`} />
          ))}
          {isRoleCucinaOrLab && visibleRoleDefaults.map((item) => (
            <Tile key={item.key} icon={item.icon} label={item.label} gradient={item.gradient} shadow={item.shadow}
              badge={badges[item.key] || 0}
              onClick={() => navigate(item.path)} testId={`func-btn-${item.key}`} />
          ))}
        </div>

        {/* Assigned tiles */}
        {assignedKeys.length > 0 && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-widest mt-6 mb-3 px-1" style={{ color: "#9ca3af" }}>Le tue attività</p>
            <div className="grid grid-cols-3 gap-3">
              {assignedKeys.map((key) => {
                const item = ASSIGNED[key];
                return <Tile key={key} icon={item.icon} label={item.label} gradient={item.gradient} shadow={item.shadow}
                  badge={badges[key] || 0}
                  onClick={() => navigate(item.path)} testId={`func-btn-${key}`} />;
              })}
            </div>
          </>
        )}

        <p className="text-center text-[11px] mt-8" style={{ color: "#c0c0c0" }}>
          {user?.ruolo === "cucina" ? "Operatore Cucina" : user?.ruolo === "sala" ? "Operatore Sala" : user?.ruolo}
        </p>
      </main>
      <FeedbackButton />
    </div>
  );
};

export default OperatoreDashboard;
